import React, { useState } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { formatNumber } from '../../utils/formatters';

// Helper function to format period labels
const formatPeriodLabel = (period, periodType) => {
  const date = new Date(period);
  
  if (periodType === 'day') {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }
  if (periodType === 'week') {
    return `W${getWeekNumber(date)} ${date.toLocaleDateString('en-GB', { month: 'short' })}`;
  }
  if (periodType === 'month') {
    return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  }
  return period;
};

// Get week number from date
const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const TimelineChart = ({ 
  data, 
  period, 
  onPeriodChange, 
  compact = false 
}) => {
  const [chartType, setChartType] = useState('area');
  const [selectedMetrics, setSelectedMetrics] = useState(['views', 'engagement']);

  const metrics = [
    { key: 'views', label: 'Views', color: '#6366F1' },
    { key: 'likes', label: 'Likes', color: '#EC4899' },
    { key: 'comments', label: 'Comments', color: '#F59E0B' },
    { key: 'avgCcv', label: 'Avg CCV', color: '#8B5CF6' },
    { key: 'peakCcv', label: 'Peak CCV', color: '#14B8A6' },
    { key: 'watchHours', label: 'Watch Hours', color: '#10B981' },
    { key: 'contentCount', label: 'Content', color: '#6B7280' }
  ];

  // Calculate engagement as a composite metric
  const chartData = data.map(item => ({
    ...item,
    engagement: item.likes + item.comments + item.shares,
    periodLabel: formatPeriodLabel(item.period, period)
  }));

  const toggleMetric = (metricKey) => {
    if (selectedMetrics.includes(metricKey)) {
      if (selectedMetrics.length > 1) {
        setSelectedMetrics(selectedMetrics.filter(m => m !== metricKey));
      }
    } else {
      setSelectedMetrics([...selectedMetrics, metricKey]);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3">
        <p className="font-medium text-gray-900 mb-2">{label}</p>
        {payload.map((item, index) => (
          <div key={index} className="flex items-center justify-between space-x-4 text-sm">
            <div className="flex items-center">
              <div 
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-gray-600">{item.name}</span>
            </div>
            <span className="font-medium text-gray-900">
              {formatNumber(item.value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: compact 
        ? { top: 10, right: 10, left: 0, bottom: 0 }
        : { top: 20, right: 30, left: 20, bottom: 10 }
    };

    const activeMetrics = metrics.filter(m => selectedMetrics.includes(m.key));

    if (chartType === 'line') {
      return (
        <LineChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis 
            dataKey="periodLabel" 
            tick={{ fill: '#6B7280', fontSize: 12 }}
            tickLine={false}
          />
          <YAxis 
            tick={{ fill: '#6B7280', fontSize: 12 }}
            tickLine={false}
            tickFormatter={formatNumber}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {activeMetrics.map(metric => (
            <Line
              key={metric.key}
              type="monotone"
              dataKey={metric.key}
              name={metric.label}
              stroke={metric.color}
              strokeWidth={2}
              dot={{ fill: metric.color, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
          ))}
        </LineChart>
      );
    }

    if (chartType === 'bar') {
      return (
        <BarChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis 
            dataKey="periodLabel" 
            tick={{ fill: '#6B7280', fontSize: 12 }}
            tickLine={false}
          />
          <YAxis 
            tick={{ fill: '#6B7280', fontSize: 12 }}
            tickLine={false}
            tickFormatter={formatNumber}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {activeMetrics.map(metric => (
            <Bar
              key={metric.key}
              dataKey={metric.key}
              name={metric.label}
              fill={metric.color}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      );
    }

    // Default: Area chart
    return (
      <AreaChart {...commonProps}>
        <defs>
          {activeMetrics.map(metric => (
            <linearGradient key={metric.key} id={`gradient-${metric.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={metric.color} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={metric.color} stopOpacity={0}/>
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
        <XAxis 
          dataKey="periodLabel" 
          tick={{ fill: '#6B7280', fontSize: 12 }}
          tickLine={false}
        />
        <YAxis 
          tick={{ fill: '#6B7280', fontSize: 12 }}
          tickLine={false}
          tickFormatter={formatNumber}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        {activeMetrics.map(metric => (
          <Area
            key={metric.key}
            type="monotone"
            dataKey={metric.key}
            name={metric.label}
            stroke={metric.color}
            strokeWidth={2}
            fill={`url(#gradient-${metric.key})`}
          />
        ))}
      </AreaChart>
    );
  };

  if (compact) {
    return (
      <div className="card-pink-accent bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Performance Over Time</h3>
          <select
            value={period}
            onChange={(e) => onPeriodChange(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-sc-pink focus:border-sc-pink"
          >
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </select>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  return (
    <div className="card-pink-accent bg-white rounded-xl border border-gray-200 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Performance Timeline</h3>
        
        <div className="mt-4 md:mt-0 flex items-center space-x-4">
          {/* Period selector */}
          <select
            value={period}
            onChange={(e) => onPeriodChange(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-sc-pink focus:border-sc-pink"
          >
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </select>
          
          {/* Chart type selector */}
          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
            {['area', 'line', 'bar'].map((type) => (
              <button
                key={type}
                onClick={() => setChartType(type)}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  chartType === type
                    ? 'bg-sc-pink text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Metric toggles */}
      <div className="flex flex-wrap gap-2 mb-6">
        {metrics.map(metric => (
          <button
            key={metric.key}
            onClick={() => toggleMetric(metric.key)}
            className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              selectedMetrics.includes(metric.key)
                ? 'text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            style={selectedMetrics.includes(metric.key) ? { backgroundColor: metric.color } : {}}
          >
            {metric.label}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>

      {/* Summary stats */}
      <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <p className="text-sm text-gray-500">Total Views</p>
          <p className="text-xl font-bold text-gray-900">
            {formatNumber(chartData.reduce((sum, d) => sum + d.views, 0))}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Total Engagement</p>
          <p className="text-xl font-bold text-gray-900">
            {formatNumber(chartData.reduce((sum, d) => sum + d.engagement, 0))}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Avg Weekly Views</p>
          <p className="text-xl font-bold text-gray-900">
            {formatNumber(Math.round(chartData.reduce((sum, d) => sum + d.views, 0) / (chartData.length || 1)))}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Peak Period</p>
          <p className="text-xl font-bold text-gray-900">
            {chartData.length > 0 
              ? chartData.reduce((max, d) => d.views > max.views ? d : max, chartData[0])?.periodLabel 
              : '-'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TimelineChart;
